/** @jsx jsx */
import * as React from "react";
import { connect, Dispatch } from "react-redux";
import { IAppState } from "../models/IAppState";
import { AppConstants, SHIFT_STATUS, CALENDAR_DAYS } from "../utils/AppConstants";
import IDoctorProfile from "../models/IDoctorProfile";
import { IRosterRequest } from "../models/Roster/IRosterRequest";
import { RosterActions } from "../actions/RosterActions";
import { IResourceSchedule, ResourceScheduleResponse } from "../models/Roster/RosterResponse";
import * as moment from "moment";
import { RosterCard } from "../components/Roster/RosterCard";
import { AddRosterModal } from "../components/Roster/AddRosterModal";
import { ITimeInterval } from "../models/Roster/ITimeInterval";
import { IAddRosterRequest } from "../models/Roster/IAddRosterRequest";
import { CopyRosterModal } from "../components/Roster/CopyRosterModal";
import { EditRosterModal } from "../components/Roster/EditRosterModa";
import AppUtils from "../utils/AppUtils";
import { Tracker } from "../utils/Tracker";
import { EventNames } from "../utils/EventTrackerConstants";
import { AlertModal } from "../components/Roster/AlertModal";
import LocalStorageUtils from "../utils/LocalStorageUtils";
import { rosterStyles } from "../styles/roster/mobile/roster";
import { css, jsx } from "@emotion/core"
import { NewFeatureBanner, NewFeatureBannerCta } from "../components/NewFeatureBanner";
import { RosterCalendarStyles, rosterInnerContainer } from "../styles/components/Roster/RosterCalendar";
import { Link } from "react-router";
import { RouteUtils } from "../routes";

interface IRosterProps {
    selectedDoctor: IDoctorProfile;
    fetchExistingRoster(params: IRosterRequest): Promise<IResourceSchedule[]>;
    updateRoster(params: IAddRosterRequest[]): Promise<ResourceScheduleResponse>;
    getAllowedAgentTypesForAutomatedWorkingHours(): Promise<string[]>;
}

interface IRosterState {
    startEpoch: number;
    endEpoch: number;
    dateRosterMap: { [date: string]: IResourceSchedule[] };
    isEditModalOpen: boolean;
    isAddRosterModalOpen: boolean;
    dateCurrentlyAdding: string;
    isCopyShiftModalOpen: Boolean;
    shiftToCopy: ITimeInterval;
    datesAvailableToCopyTo: string[];
    exisitngShiftsToEdit: IResourceSchedule[];
    expandedCardIndex: number;
    errorMsg: string;
    calendarEditableDays: number;
    totalCalendarDays: number;
    expandedDate: string;
    allowedAgentTypes: string[];
}

class RosterContainer extends React.Component<IRosterProps, IRosterState> {

    private toggleButtonInfos = [{ display: "Week", meta: AppConstants.DAYS_IN_A_WEEK }, { display: "Day", meta: 1 }];
    private fetchCurrentRosterParams: IRosterRequest;
    private newFeatureStepsList: string[] = ["Set Working hours for your daily consultations"];
    private newFeaatureBannerCta: NewFeatureBannerCta[] = [
        {
            text: "How to Use",
            onClick: () => {
                Tracker.track(EventNames.Working_Hours_Plan_How_To_Set);
                window.open("https://youtu.be/LQGKJh1j0KE");
            }
        }
    ]

    constructor(props: IRosterProps) {
        super(props);
        this.state = {
            dateRosterMap: null,
            startEpoch: null,
            endEpoch: null,
            isAddRosterModalOpen: false,
            isEditModalOpen: false,
            dateCurrentlyAdding: null,
            shiftToCopy: null,
            datesAvailableToCopyTo: [],
            isCopyShiftModalOpen: false,
            exisitngShiftsToEdit: null,
            expandedCardIndex: -1,
            errorMsg: null,
            calendarEditableDays: 15,
            totalCalendarDays: null,
            expandedDate: null,
            allowedAgentTypes: null
        };
        this.toggleDayWeekView = this.toggleDayWeekView.bind(this);
        this.changeDate = this.changeDate.bind(this);
        this.addRosterShifts = this.addRosterShifts.bind(this);
        this.updateRoster = this.updateRoster.bind(this);
        this.checkConflictingShifts = this.checkConflictingShifts.bind(this);
        this.closeCopyModal = this.closeCopyModal.bind(this);
        this.openEditModal = this.openEditModal.bind(this);
        this.closeEditModal = this.closeEditModal.bind(this);
        this.setExpandedCardIndex = this.setExpandedCardIndex.bind(this);
        this.closeErrorModal = this.closeErrorModal.bind(this);
    }
    componentDidMount() {
        const { selectedDoctor } = this.props;
        const { calendarEditableDays } = this.state;
        const currentDate: number = new Date().setHours(0, 0, 0, 0);
        const startEpoch: number = AppUtils.getStartOfTheWeekFromMonday(new Date());

        const endEpoch = AppUtils.getEndOfTheWeek(moment(currentDate).add(calendarEditableDays - 1, "days").toDate());
        const totalCalendarDays = moment(endEpoch).diff(startEpoch, "days") + 1;
        this.setState({ endEpoch, startEpoch, totalCalendarDays }, () => {
            Tracker.pageOpened("Roster");
            this.fetchRoster();
        });
        this.props.getAllowedAgentTypesForAutomatedWorkingHours().then(allowedAgentTypes => this.setState({ allowedAgentTypes }));
    }

    componentWillUnmount() {
        Tracker.pageClosed("Roster")
    }

    private toggleDayWeekView(): void {

    }

    private changeDate(action: "NEXT" | "PREVIOUS"): void {
        const offsetValues = AppConstants.DAYS_IN_A_WEEK * AppConstants.EPOCH_MS_IN_ONE_DAY;
        let { startEpoch, endEpoch } = this.state;
        if (action === "NEXT") {
            startEpoch = startEpoch + offsetValues;
            endEpoch = endEpoch + offsetValues;

        } else {
            startEpoch = startEpoch - offsetValues;
            endEpoch = endEpoch - offsetValues;
        }
        this.setState({ startEpoch, endEpoch }, () => this.fetchRoster());
    }

    private fetchRoster(): void {
        const { startEpoch, endEpoch, totalCalendarDays } = this.state;
        this.fetchCurrentRosterParams = {
            startDate: moment(startEpoch).format(AppConstants.dateFormat),
            endDate: moment(endEpoch).format(AppConstants.dateFormat),
            resourceId: this.props.selectedDoctor.id
        };
        const dateRosterMap: { [date: string]: IResourceSchedule[] } = {};
        for (let dayCount = 0; dayCount <= totalCalendarDays; dayCount++) {
            const formattedDate: string = moment(startEpoch).add(dayCount, "days").format(AppConstants.dateFormat);
            dateRosterMap[formattedDate] = []
        }
        this.props.fetchExistingRoster(this.fetchCurrentRosterParams)
            .then((currentRoster: IResourceSchedule[]) => {
                currentRoster = AppUtils.parseRosterDataWithBreaksToWIthoutBreaks(currentRoster).filter(({ scheduleStatus }) => scheduleStatus === SHIFT_STATUS.ACTIVE);
                currentRoster.forEach(workigSlot => {
                    const workingDate = moment(workigSlot.workingDayDate).format(AppConstants.dateFormat);

                    if (dateRosterMap[workingDate]) {
                        dateRosterMap[workingDate].push(workigSlot);
                        dateRosterMap[workingDate] = dateRosterMap[workingDate].sort((shift1: IResourceSchedule, shift2: IResourceSchedule) => {
                            const startTime1 = AppUtils.getEpochFromDateAndTime(shift1.workingDayDate, shift1.startTime);
                            const startTime2 = AppUtils.getEpochFromDateAndTime(shift2.workingDayDate, shift2.startTime);
                            return startTime1 - startTime2;
                        });
                    }
                });
                this.setState({ dateRosterMap });

            })
            .catch((err: Error) => {
                console.error(err);
                this.setState({ errorMsg: `Failed to fetch working hours:  ${err}` });

            });
    }



    private checkConflictingShifts(intervalToCheck: ITimeInterval, datesToCheckOn: string[]): IResourceSchedule[] {
        const { dateRosterMap } = this.state;
        let currentPresentIntervalsForGivenDates: IResourceSchedule[] = [];
        datesToCheckOn.forEach(date => currentPresentIntervalsForGivenDates = currentPresentIntervalsForGivenDates.concat(dateRosterMap[date]));
        const conflictingShifts = currentPresentIntervalsForGivenDates.filter(({ startTime, endTime, workingDayDate }) => {
            const startEpochOfExisitingShift = AppUtils.getEpochFromDateAndTime(workingDayDate, startTime);
            const endEpochOfExisitingShift = AppUtils.getEpochFromDateAndTime(workingDayDate, endTime);
            const startEpochOfShiftToCheck = AppUtils.getEpochFromDateAndTime(workingDayDate, intervalToCheck.startTime);
            const endEpochOfShiftToCheck = AppUtils.getEpochFromDateAndTime(workingDayDate, intervalToCheck.endTime);

            if (moment(startEpochOfShiftToCheck).isBefore(moment(endEpochOfExisitingShift)) && moment(endEpochOfShiftToCheck).isAfter(moment(startEpochOfExisitingShift)))
                return true;
            return false;
        });
        return conflictingShifts;
    }

    private addRosterShifts(shifts: ITimeInterval[], date: string): void {
        const { selectedDoctor } = this.props;
        const workingDayDate: number = moment(date, AppConstants.dateFormat).unix() * 1000 - new Date().getTimezoneOffset() * 60 * 1000;;
        const addRosterParams: IAddRosterRequest[] = shifts.map(({ startTime, endTime }) => {
            return {
                resourceDeallocationScheduleList: [],
                startTime,
                endTime,
                resourceId: selectedDoctor.id,
                resourceType: AppConstants.RESOURCE_TYPE_FOR_ROSTER,
                centerId: selectedDoctor.agentCenterMapping && selectedDoctor.agentCenterMapping[0] && selectedDoctor.agentCenterMapping[0].centerId,
                scheduleStatus: SHIFT_STATUS.ACTIVE,
                workingDayDate: workingDayDate,
                availableOnline: true
            }
        });
        this.setState({ isAddRosterModalOpen: false });
        this.props.updateRoster(addRosterParams)
            .then(res => {
                this.fetchRoster();
            })
            .catch(err => {
                this.setState({ errorMsg: `Failed to add working hours:  ${err}` });
            })
    }

    private openCopyModal(shiftToCopy: ITimeInterval, date: string): void {
        LocalStorageUtils.setClickEvents({ field: EventNames.Copy_Hours, widget: EventNames.Day_Widget, flow: EventNames.Working_Hours, section: EventNames.Working_Hours })
        const { dateRosterMap } = this.state;
        const dateList: string[] = dateRosterMap ? Object.keys(dateRosterMap) : [];
        const currentDateIndex = dateList.findIndex(key => key === moment(new Date().setHours(0, 0, 0, 0)).format("DD-MM-YYYY"));
        const rosterEditTillDateIndex = dateList.findIndex(key => key === moment(new Date().setHours(0, 0, 0, 0)).add(this.state.calendarEditableDays - 1, "days").format("DD-MM-YYYY"));
        let datesAvailableToCopyTo: string[] = dateList.slice(currentDateIndex + 1, rosterEditTillDateIndex + 1);
        let selectedDateIndex = datesAvailableToCopyTo.findIndex(d => d === date);
        datesAvailableToCopyTo.splice(selectedDateIndex, 1)
        this.setState({ isCopyShiftModalOpen: true, shiftToCopy, datesAvailableToCopyTo, dateCurrentlyAdding: date });
        Tracker.track(EventNames.Copy_Hours_Clicked)
    }

    private closeCopyModal(): void {
        LocalStorageUtils.setClickEvents({ field: EventNames.Close, widget: EventNames.Copy_Hours, flow: EventNames.Working_Hours, section: EventNames.Working_Hours })
        this.setState({ isCopyShiftModalOpen: false, shiftToCopy: null, datesAvailableToCopyTo: [] });
    }
    private updateRoster(addRosterParams: IAddRosterRequest[]): void {
        this.closeCopyModal();
        this.closeEditModal();
        this.props.updateRoster(addRosterParams)
            .then(res => {
                this.fetchRoster();
            })
            .catch(err => {
                this.setState({ errorMsg: `Failed to update working hours:  ${err}` });
            });
    }

    private openEditModal(date: string): void {
        LocalStorageUtils.setClickEvents({ field: "Edit working Hours", widget: EventNames.Day_Widget, flow: EventNames.Working_Hours, section: EventNames.Working_Hours })
        Tracker.track(EventNames.Edit_Roster_Clicked)
        this.setState({ isEditModalOpen: true, exisitngShiftsToEdit: this.state.dateRosterMap[date], dateCurrentlyAdding: date });
    }

    private closeEditModal(): void {
        LocalStorageUtils.setClickEvents({ field: EventNames.Close, widget: EventNames.Edit, flow: EventNames.Working_Hours, section: EventNames.Working_Hours })
        Tracker.track(EventNames.Edit_Modal_Closed)
        this.setState({ isEditModalOpen: false, exisitngShiftsToEdit: null });
    }

    private setExpandedCardIndex(index: number, expandedDate?: any): void {
        const { expandedCardIndex } = this.state;
        if (index === expandedCardIndex) {
            this.setState({ expandedCardIndex: -1, expandedDate: null });
        } else {
            this.setState({ expandedCardIndex: index, expandedDate });
        }

    }

    openAddHoursModal = (date: string) => {
        LocalStorageUtils.setClickEvents({ field: EventNames.Add_Working_Hours, widget: EventNames.Day_Widget, flow: EventNames.Working_Hours, section: EventNames.Working_Hours })
        Tracker.track(EventNames.Add_Working_Hours_Clicked)
        this.setState({ isAddRosterModalOpen: true, dateCurrentlyAdding: date })
    }
    private closeErrorModal(): void {
        this.setState({ errorMsg: null });
    }
    private opneHowToVideo = () => {
        Tracker.track(EventNames.Working_Hours_Plan_How_To_Set)
        window.open("https://youtu.be/LQGKJh1j0KE");
    }

    getDayWiseDates = (dateList: string[]) => {
        let dayWiseDates: any = dateList.length > 0 && { [CALENDAR_DAYS.Monday.momentDayValue]: [], [CALENDAR_DAYS.Tuesday.momentDayValue]: [], [CALENDAR_DAYS.Wednesday.momentDayValue]: [], [CALENDAR_DAYS.Thursday.momentDayValue]: [], [CALENDAR_DAYS.Friday.momentDayValue]: [], [CALENDAR_DAYS.Saturday.momentDayValue]: [], [CALENDAR_DAYS.Sunday.momentDayValue]: [] }
        dateList.forEach(rosterDate => {
            let day = moment(rosterDate, "DD-MM-YYYY").format("dddd");
            dayWiseDates[day].push(rosterDate);
        })

        return dayWiseDates;
    }

    getDisableDates = () => {
        const { dateRosterMap } = this.state;
        if (dateRosterMap) {
            const currentDateIndex = Object.keys(dateRosterMap).findIndex(key => key === moment(new Date().setHours(0, 0, 0, 0)).format("DD-MM-YYYY"));
            const rosterEditTillDate = Object.keys(dateRosterMap).findIndex(key => key === moment(new Date().setHours(0, 0, 0, 0)).add(this.state.calendarEditableDays - 1, "days").format("DD-MM-YYYY"));
            let daysToDisable: any = [];
            daysToDisable.push(Object.keys(dateRosterMap).slice(4, currentDateIndex).slice(rosterEditTillDate, Object.keys(dateRosterMap).length - 1));
            return daysToDisable;
        }
    }
    render() {
        const { dateRosterMap, isAddRosterModalOpen, dateCurrentlyAdding, isCopyShiftModalOpen, datesAvailableToCopyTo,
            shiftToCopy, isEditModalOpen, exisitngShiftsToEdit, expandedCardIndex, startEpoch, errorMsg, calendarEditableDays, expandedDate, allowedAgentTypes } = this.state;
        const { selectedDoctor } = this.props;
        const dateList: string[] = dateRosterMap ? Object.keys(dateRosterMap) : [];
        const isPreviosDisabled: boolean = startEpoch <= new Date().setHours(0, 0, 0, 0);
        const isMobile: boolean = AppUtils.getIsMobile();
        const dayWiseDates: any = this.getDayWiseDates(dateList);
        const currentDate = moment(new Date().setHours(0, 0, 0, 0));
        const rosterEditTillDate = moment(new Date().setHours(0, 0, 0, 0)).add(calendarEditableDays - 1, "days");
        const isAutomatedWorkingHoursAllowedForAgent = allowedAgentTypes && allowedAgentTypes.includes(selectedDoctor.type);

        return (
            <div className="doctor-dashboard" >
                {/* {!isMobile &&
                    <div css={rosterStyles.rosterBannerContainer}>
                        <div>
                            <img src="/images/clock-icon.svg" alt="working hours banner" css={rosterStyles.bannerClock} />
                        </div>
                        <div>
                            <h3 css={rosterStyles.bannerHeader}>Working Hours</h3>
                            <div css={rosterStyles.bannerText}>Set working hours for your daily consultations</div>
                        </div>
                        <div css={rosterStyles.bannerCtaContainer}>
                            <button css={rosterStyles.bannerCta} onClick={this.opneHowToVideo}>How to Set</button>
                        </div>
                    </div>
                } */}
                <div css={rosterStyles.topNav} className="dashboard-top-nav">
                    {/* <div className="day-week-selector"> */}
                    {/* <ToggleButtonGroup toggleButtonInfos={this.toggleButtonInfos} startSelIdx={0}
                            onToggleButtonClick={this.toggleDayWeekView} /> */}
                    {/* </div> */}
                    <div>
                        <h3 css={rosterStyles.bannerHeader}>Working Hours</h3>
                    </div>
                    {dateList && dateList.length > 0 &&
                        <div className="date-nav">
                            {/* <img style={isPreviosDisabled ? { opacity: 0.5, pointerEvents: "none" } : {}} className="date-navigator date-left-nav" src={ORION_IMG_URL + "date-left-nav.png"} onClick={() => this.changeDate("PREVIOUS")} /> */}
                            <div className="date-nav-display">
                                <div className="date-nav-date-display brandon-bold">
                                    <span className="date-nav-text date-text-left">{moment(dateList[0], AppConstants.dateFormat).format("DD MMM")}</span>
                                    <span className="date-nav-sep"> - </span>
                                    <span className="date-nav-text date-text-right">{moment(dateList[dateList.length - 1], AppConstants.dateFormat).format("DD MMM")}</span>
                                </div>
                            </div>
                            {/* <img className="date-navigator date-right-nav" src={ORION_IMG_URL + "date-right-nav.png"} onClick={() => this.changeDate("NEXT")} /> */}
                        </div>
                    }

                    <div css={isMobile ? rosterStyles.defaultWorkingHoursAlign : rosterStyles.rightCTAContainer} className="default-working">
                        <div onClick={this.opneHowToVideo} css={rosterStyles.playContainer}>
                            <span className="glyphicon glyphicon-play-circle" css={rosterStyles.playIcon}></span>
                            <span>How To Set</span>
                        </div>
                        {isAutomatedWorkingHoursAllowedForAgent &&
                            <Link to={RouteUtils.getDefaultWorkingHoursUrl()} css={rosterStyles.defaultWorkingHoursButton} >Automated Working Hours</Link>
                        }
                    </div>
                </div>

                <div css={isMobile ? rosterStyles.rosterContainer : {}} className="roster-container">
                    {
                        isMobile && dateList.map((date: string, index: number) => {

                            return (
                                <RosterCard slotsForTheDay={dateRosterMap[date]} date={date} key={date} openEditModal={this.openEditModal}
                                    openCopyModal={(interval: ITimeInterval) => this.openCopyModal(interval, date)} onCardExpand={() => this.setExpandedCardIndex(index)}
                                    openAddHoursModal={this.openAddHoursModal}
                                    style={expandedCardIndex !== index && expandedCardIndex !== -1 ? { opacity: 0.2 } : {}} />
                            )
                        })
                    }
                </div>
                {
                    !isMobile &&
                    <div css={RosterCalendarStyles.rosterWrapper}>
                        <div css={RosterCalendarStyles.rosterOuterContainer}>
                            {Object.keys(dayWiseDates).map((day: any, index: number) => {
                                return (
                                    <div className={rosterInnerContainer} key={`${day}-${index}`}>
                                        <div css={RosterCalendarStyles.rosterCalendarLabel}>{day}</div>
                                        {dayWiseDates[day].map((date: string) => {
                                            return <RosterCard slotsForTheDay={dateRosterMap[date]} date={date} key={date} openEditModal={this.openEditModal}
                                                openCopyModal={(interval: ITimeInterval) => this.openCopyModal(interval, date)} onCardExpand={() => this.setExpandedCardIndex(index, date)}
                                                openAddHoursModal={this.openAddHoursModal}
                                                style={expandedCardIndex !== -1 && expandedDate != date ? { opacity: 0.2 } : {}}
                                                shouldDisable={!(moment(date, "DD-MM-YYYY").startOf('day').isBetween(currentDate, rosterEditTillDate, undefined, "[]"))}
                                            />
                                        })}
                                    </div>
                                )
                            })
                            }
                        </div>
                    </div>
                }
                {
                    isAddRosterModalOpen &&
                    <AddRosterModal toggleForceCloseModal={() => this.setState({ isAddRosterModalOpen: false })} title="Add Working Hours"
                        date={dateCurrentlyAdding} addRosterShifts={this.addRosterShifts} selectedDoctor={selectedDoctor} />
                }
                {
                    isCopyShiftModalOpen &&
                    <CopyRosterModal shift={shiftToCopy} datesAvailableToCopy={datesAvailableToCopyTo} selectedDoctor={selectedDoctor}
                        closeCopyModal={this.closeCopyModal} copyShifts={this.updateRoster}
                        checkConflictingShifts={this.checkConflictingShifts} dayWiseDates={dayWiseDates} dateCurrentlyCopying={dateCurrentlyAdding} title="Copy Working Hours" />
                }
                {
                    isEditModalOpen &&
                    <EditRosterModal toggleForceCloseModal={this.closeEditModal} title="Edit Working Hours" updateRoster={this.updateRoster}
                        date={dateCurrentlyAdding} editRosterShifts={this.updateRoster} selectedDoctor={selectedDoctor} exisitngShifts={exisitngShiftsToEdit} />
                }
                {
                    errorMsg &&
                    <AlertModal header={<div>Failed</div>} body={<div>{errorMsg}</div>} cancelBtnProps={{ clickHandler: this.closeErrorModal, text: "Cancel" }}></AlertModal>
                }

            </div >
        )
    }

}

const mapStateToProps = (state: IAppState) => {

    return {
        selectedDoctor: state.doctorProfile
    };
}


const mapDispatchToProps = (dispatch: Dispatch<any>) => {

    return {
        fetchExistingRoster: (params: IRosterRequest) => RosterActions.fetchDoctorRoster(params),
        updateRoster: (params: IAddRosterRequest[]) => RosterActions.updateRoster(params),
        getAllowedAgentTypesForAutomatedWorkingHours: () => RosterActions.getAllowedAgentTypesForAutomatedWorkingHours()
    };
}

export const Roster = connect(mapStateToProps, mapDispatchToProps)(RosterContainer);